/**
 * Converts the pa_embroidery-type variation <select> (single-product
 * page) into a row of clickable image-thumbnail swatches — a small
 * reference photo per embroidery style, set per-term from wp-admin
 * (Products → Attributes → Type of Embroidery → Configure terms →
 * "Embroidery Image"), so a shopper can see which stitching style
 * they're picking at a glance instead of reading a plain text list.
 *
 * Same presentation-layer approach as color-swatches.js: the underlying
 * <select> stays the real source of truth WooCommerce's own
 * variation.js reads from/updates — a swatch click just sets its value
 * and dispatches a native "change" event.
 *
 * Each <option> carries a data-embroidery-image attribute added
 * server-side (craftis_add_embroidery_swatch_data() in
 * inc/woocommerce-hooks.php, reading each term's
 * craftis_embroidery_image term meta). A term with no image assigned
 * yet falls back to the plain styled dropdown (variation-pills.js also
 * explicitly skips this attribute, so that fallback is the select
 * itself, not pills) rather than showing a broken/empty swatch.
 */

function buildEmbroiderySwatches( select ) {
	const wrapper = select.closest( 'td.value' ) || select.parentElement;
	if ( ! wrapper || wrapper.querySelector( '.craftis-embroidery-swatches' ) ) {
		return;
	}

	const options = Array.from( select.options ).filter( ( option ) => option.value !== '' );
	const hasImageForAll = options.every( ( option ) => option.dataset.embroideryImage );

	if ( ! options.length || ! hasImageForAll ) {
		return;
	}

	const swatchList = document.createElement( 'div' );
	swatchList.className = 'craftis-embroidery-swatches';
	swatchList.setAttribute( 'role', 'radiogroup' );

	options.forEach( ( option ) => {
		const swatch = document.createElement( 'button' );
		swatch.type = 'button';
		swatch.className = 'craftis-embroidery-swatches__item';
		swatch.setAttribute( 'role', 'radio' );
		swatch.setAttribute( 'aria-checked', option.selected ? 'true' : 'false' );
		swatch.dataset.value = option.value;

		const img = document.createElement( 'img' );
		img.src = option.dataset.embroideryImage;
		img.alt = option.textContent.trim();
		img.loading = 'lazy';

		const label = document.createElement( 'span' );
		label.className = 'craftis-embroidery-swatches__label';
		label.textContent = option.textContent.trim();

		swatch.appendChild( img );
		swatch.appendChild( label );

		if ( option.selected ) {
			swatch.classList.add( 'is-selected' );
		}

		swatch.addEventListener( 'click', () => {
			if ( select.value === option.value ) {
				return;
			}

			select.value = option.value;
			select.dispatchEvent( new Event( 'change', { bubbles: true } ) );

			swatchList.querySelectorAll( '.craftis-embroidery-swatches__item' ).forEach( ( item ) => {
				item.classList.remove( 'is-selected' );
				item.setAttribute( 'aria-checked', 'false' );
			} );

			swatch.classList.add( 'is-selected' );
			swatch.setAttribute( 'aria-checked', 'true' );
		} );

		swatchList.appendChild( swatch );
	} );

	select.classList.add( 'craftis-embroidery-swatches__native-select' );
	wrapper.appendChild( swatchList );

	// Same "Clear" relocation as color-swatches.js — moves WooCommerce's
	// own reset_variations link to sit at the end of the swatch row
	// instead of on its own line below it.
	const resetLink = wrapper.querySelector( 'a.reset_variations' );
	if ( resetLink ) {
		swatchList.appendChild( resetLink );
	}

	select.addEventListener( 'change', () => {
		swatchList.querySelectorAll( '.craftis-embroidery-swatches__item' ).forEach( ( item ) => {
			const isSelected = item.dataset.value === select.value;
			item.classList.toggle( 'is-selected', isSelected );
			item.setAttribute( 'aria-checked', isSelected ? 'true' : 'false' );
		} );
	} );
}

function initEmbroiderySwatches() {
	document
		.querySelectorAll( 'select#pa_embroidery-type, select[data-attribute_name="attribute_pa_embroidery-type"]' )
		.forEach( buildEmbroiderySwatches );
}

document.addEventListener( 'DOMContentLoaded', initEmbroiderySwatches );

document.body.addEventListener( 'wc_variation_form', initEmbroiderySwatches );
